// ENG-6041: conversation eval-score aggregation for the platform-admin Dashboard.
//
// Pure, DB-agnostic, browser-safe aggregation that turns host-evaluated
// conversation rows into a "did the agents actually help" signal. Callers (the
// cross-org admin Dashboard via the Next.js webapp) fetch evaluated rows over a
// window and hand them here; keeping the bucketing + moving-average maths pure
// makes it unit-testable without a database. Mirrors ratings/kanban-ratings.ts
// (ENG-6033) — the kanban chart answers "are agents improving (human thumbs)?",
// this one answers "are conversations succeeding (Haiku eval)?".
//
// SCORE MODEL:
//   - Each completed end-user conversation is scored 0-100 HOST-SIDE by a cheap
//     Haiku pass (the transcript never leaves the host; see migration
//     20260604000007_conversation_evaluation.sql). Score here is a TRAILING
//     28-DAY MOVING AVERAGE of those eval_scores, anchored on `evaluated_at`.
//   - The MA window is FIXED at 28d regardless of the display period. The period
//     (24h/7d/30d) only controls how far back the chart is drawn and how finely
//     it's sampled — the CALLER must fetch `period + 28d` of rows so each sample
//     point can average the 28 days of evaluations ending at that point.
//   - A sample point with zero evaluations in its trailing window scores `null`
//     (a genuine gap — the UI must NOT interpolate across it).
//   - `n` (the count behind each score) travels with every score so a 2-eval
//     average is never silently displayed as authoritative as a 200-eval one.

import type { ConversationMetricsPeriod } from './metrics.js';

/** Trailing window for the moving average: 4 weeks, fixed (independent of the display period). */
export const EVAL_SCORE_MA_WINDOW_MS = 28 * 24 * 60 * 60 * 1000;
export const EVAL_SCORE_MA_WINDOW_DAYS = 28;

/**
 * A `trend` is only emitted when its delta clears this band, so noise reads as
 * "flat". 2 points on the 0-100 scale (the analogue of 0.05 on the ±1 scale).
 */
const TREND_EPSILON = 2;

/** Display window length + sampling granularity per period (mirrors conversation metrics). */
const PERIOD_CONFIG: Record<
  ConversationMetricsPeriod,
  { windowMs: number; bucket: 'hour' | 'day' }
> = {
  '24h': { windowMs: 24 * 60 * 60 * 1000, bucket: 'hour' },
  '7d': { windowMs: 7 * 24 * 60 * 60 * 1000, bucket: 'day' },
  '30d': { windowMs: 30 * 24 * 60 * 60 * 1000, bucket: 'day' },
};

/**
 * One evaluated conversation row. The caller filters to `eval_score IS NOT NULL`
 * and fetches back to `periodStart - 28d` so the trailing MA is correct from the
 * first displayed bucket. `code_name`/`display_name` ride along so the helper
 * stays free of any agent lookup.
 */
export interface ConversationEvalScoreRow {
  agent_id: string;
  code_name: string;
  display_name: string | null;
  /** Owning organisation's display name — shown after the agent name on the cross-org admin surface. */
  org_name: string | null;
  /** Host-side Haiku 0-100 success score. Out-of-range values are ignored defensively. */
  eval_score: number;
  /**
   * Coarse verdict bucket the host scorer assigned alongside the 0-100 score
   * (ENG-7128). Optional: when absent (an older caller that doesn't select it),
   * the verdict-rate summary simply reports zero/null and the dashboard falls
   * back to the mean-only view. Unrecognised values are ignored defensively.
   */
  eval_verdict?: ConversationEvalVerdict | null;
  evaluated_at: string;
}

/** Coarse host-scorer verdict bucket (mirrors the DB CHECK on conversations.eval_verdict). */
export type ConversationEvalVerdict = 'success' | 'partial' | 'failure';

function isEvalVerdict(v: unknown): v is ConversationEvalVerdict {
  return v === 'success' || v === 'partial' || v === 'failure';
}

/** One sample of the trailing-28d moving average. */
export interface EvalScoreTrendPoint {
  /** ISO sample timestamp (UTC bucket start). */
  bucket: string;
  /** Trailing-28d mean eval_score ending at `bucket`; null when none — a real gap, do NOT interpolate. */
  score: number | null;
  /** Evaluations inside the trailing window — the sample size behind `score`. */
  n: number;
}

export type EvalScoreTrend = 'up' | 'flat' | 'down';

/** Per-agent standing as of `now`, for the ranked table beneath the org line. */
export interface AgentEvalScoreSummary {
  agentId: string;
  codeName: string;
  displayName: string | null;
  /** Owning organisation's display name (cross-org disambiguation); null if unknown. */
  orgName: string | null;
  /** Trailing-28d mean eval_score as of `now`; null if no evaluations in the last 28d. */
  score: number | null;
  /** Evaluations in the trailing-28d window as of `now` (the n behind `score`). */
  evaluatedCount: number;
  /**
   * Current display period's mean vs the immediately-prior period's mean
   * (non-overlapping windows): up/flat/down. null (rendered "—") when either
   * period holds no evaluation, i.e. not enough data to compare (ENG-7231).
   */
  trend: EvalScoreTrend | null;
}

export interface ConversationEvalScoresResult {
  period: ConversationMetricsPeriod;
  bucket: 'hour' | 'day';
  period_start: string;
  ma_window_days: number;
  /** Org-wide trailing-28d MA sampled across the display window (continuous axis; gaps are null). */
  series: EvalScoreTrendPoint[];
  /** Per-agent standing as of `now`, ranked best-first. */
  perAgent: AgentEvalScoreSummary[];
  /** Evaluations across all agents inside the trailing-28d window as of `now`. */
  overallEvaluatedCount: number;
  /**
   * Verdict-rate summary over the same trailing-28d window as of `now` (ENG-7128).
   * The headline mean score is an average of a strict 0-100 grader and is pinned
   * mid-range by a large "partial" bucket; these rates answer the more intuitive
   * "did we help?" question (success / total and (success+partial) / total) so
   * the dashboard isn't read as a completion percentage it never was.
   */
  verdictMix: VerdictMix;
}

/** Verdict-bucket counts + derived helped-rates over a window. */
export interface VerdictMix {
  success: number;
  partial: number;
  failure: number;
  /** Rows in-window carrying a recognised verdict (success + partial + failure). */
  total: number;
  /** success / total as a 0-100 integer; null when no verdicts are in-window. */
  successRate: number | null;
  /** (success + partial) / total as a 0-100 integer; null when no verdicts are in-window. */
  successOrPartialRate: number | null;
}

/** Truncate a date to the start of its UTC hour or day. */
function truncateUtc(d: Date, bucket: 'hour' | 'day'): Date {
  const t = new Date(d);
  t.setUTCMinutes(0, 0, 0);
  if (bucket === 'day') t.setUTCHours(0);
  return t;
}

interface NormalisedEval {
  t: number;
  /** 0-100 eval score. */
  s: number;
  /** Recognised verdict bucket, or undefined when the caller didn't supply one. */
  v?: ConversationEvalVerdict;
}

interface TrailingStats {
  score: number | null;
  n: number;
}

/**
 * Trailing-28d mean over already-sorted evaluations across the inclusive window
 * `[endMs - 28d, endMs]`. `null` score when the window holds no evaluation.
 */
function trailingStats(sorted: NormalisedEval[], endMs: number): TrailingStats {
  const startMs = endMs - EVAL_SCORE_MA_WINDOW_MS;
  let sum = 0;
  let n = 0;
  for (const { t, s } of sorted) {
    if (t < startMs) continue;
    if (t > endMs) break; // sorted ascending — nothing later is in-window
    sum += s;
    n += 1;
  }
  return { score: n > 0 ? sum / n : null, n };
}

/**
 * Mean over already-sorted evaluations in the half-open window `(loMs, hiMs]`.
 * Used for the per-agent trend, which compares the current display period to the
 * immediately-prior one. The boundary is exclusive at `lo` / inclusive at `hi`
 * so two adjacent windows (prior = `(now-2w, now-w]`, recent = `(now-w, now]`)
 * never double-count a row sitting exactly on the shared edge.
 */
function rangeStats(sorted: NormalisedEval[], loMs: number, hiMs: number): TrailingStats {
  let sum = 0;
  let n = 0;
  for (const { t, s } of sorted) {
    if (t <= loMs) continue;
    if (t > hiMs) break; // sorted ascending — nothing later is in-window
    sum += s;
    n += 1;
  }
  return { score: n > 0 ? sum / n : null, n };
}

/**
 * Tally verdict buckets over the already-sorted evaluations in the inclusive
 * trailing window `[endMs - 28d, endMs]` and derive the helped-rates. Rows
 * without a recognised verdict don't count toward `total`, so a caller that
 * omits `eval_verdict` yields an all-zero/null mix (dashboard shows mean only).
 */
function trailingVerdictMix(sorted: NormalisedEval[], endMs: number): VerdictMix {
  const startMs = endMs - EVAL_SCORE_MA_WINDOW_MS;
  let success = 0;
  let partial = 0;
  let failure = 0;
  for (const { t, v } of sorted) {
    if (t < startMs) continue;
    if (t > endMs) break; // sorted ascending — nothing later is in-window
    if (v === 'success') success += 1;
    else if (v === 'partial') partial += 1;
    else if (v === 'failure') failure += 1;
  }
  const total = success + partial + failure;
  return {
    success,
    partial,
    failure,
    total,
    successRate: total > 0 ? Math.round((success / total) * 100) : null,
    successOrPartialRate: total > 0 ? Math.round(((success + partial) / total) * 100) : null,
  };
}

function classifyTrend(start: number | null, end: number | null): EvalScoreTrend | null {
  if (start === null || end === null) return null;
  const delta = end - start;
  if (delta > TREND_EPSILON) return 'up';
  if (delta < -TREND_EPSILON) return 'down';
  return 'flat';
}

/**
 * Aggregate evaluated conversation rows into an org-wide trailing-28d
 * moving-average series plus a per-agent standing table. `now` is injectable
 * for deterministic tests. Rows are expected to span
 * `[min(periodStart - 28d, now - 2*period), now]` — back far enough for both the
 * trailing-28d MA (score) and the prior display-period window (trend, ENG-7231);
 * rows outside those windows simply never contribute.
 */
export function aggregateConversationEvalScores(
  rows: ConversationEvalScoreRow[],
  period: ConversationMetricsPeriod,
  now: Date = new Date(),
): ConversationEvalScoresResult {
  const { windowMs, bucket } = PERIOD_CONFIG[period];
  const nowMs = now.getTime();
  const periodStart = new Date(nowMs - windowMs);

  // Normalise + sort once; per-agent buckets reuse the same ascending order so
  // trailingStats can early-break.
  const all: NormalisedEval[] = [];
  const byAgent = new Map<
    string,
    { codeName: string; displayName: string | null; orgName: string | null; evals: NormalisedEval[] }
  >();
  for (const row of rows) {
    const ts = new Date(row.evaluated_at).getTime();
    if (!Number.isFinite(ts) || ts > nowMs) continue; // ignore unparseable / future timestamps
    const s = row.eval_score;
    // Defensive: DB constrains to 0-100, but never trust the wire.
    if (!Number.isFinite(s) || s < 0 || s > 100) continue;
    const norm: NormalisedEval = { t: ts, s, v: isEvalVerdict(row.eval_verdict) ? row.eval_verdict : undefined };
    all.push(norm);

    let entry = byAgent.get(row.agent_id);
    if (!entry) {
      entry = {
        codeName: row.code_name,
        displayName: row.display_name,
        orgName: row.org_name,
        evals: [],
      };
      byAgent.set(row.agent_id, entry);
    }
    entry.evals.push(norm);
  }
  all.sort((a, b) => a.t - b.t);
  for (const entry of byAgent.values()) entry.evals.sort((a, b) => a.t - b.t);

  // Org-wide trailing-28d MA sampled across a continuous display axis.
  const stepMs = bucket === 'hour' ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
  const firstBucket = truncateUtc(periodStart, bucket).getTime();
  const series: EvalScoreTrendPoint[] = [];
  for (let t = firstBucket; t <= nowMs; t += stepMs) {
    const { score, n } = trailingStats(all, t);
    series.push({ bucket: new Date(t).toISOString(), score, n });
  }

  // Per-agent standing as of `now`. Score stays the trailing-28d MA, but the
  // trend compares the current display period to the immediately-prior one
  // (ENG-7231). The old trend compared two 28d trailing MAs a display-period
  // apart; those windows overlap by `28d - period` (21 of 28d on the 7d view),
  // so once the metric has a few weeks of history both averages converge and
  // every agent reads `flat`. Non-overlapping recent-vs-prior windows make the
  // trend reflect actual recent movement instead.
  const recentLoMs = nowMs - windowMs;
  const priorLoMs = nowMs - 2 * windowMs;
  const perAgent: AgentEvalScoreSummary[] = [];
  for (const [agentId, entry] of byAgent.entries()) {
    const atNow = trailingStats(entry.evals, nowMs);
    if (atNow.n === 0) continue; // no evaluation in the trailing window → omit
    const recent = rangeStats(entry.evals, recentLoMs, nowMs);
    const prior = rangeStats(entry.evals, priorLoMs, recentLoMs);
    perAgent.push({
      agentId,
      codeName: entry.codeName,
      displayName: entry.displayName,
      orgName: entry.orgName,
      score: atNow.score,
      evaluatedCount: atNow.n,
      // null (rendered "—") when either period has no evaluation — a genuine
      // "not enough data to compare" rather than a misleading flat.
      trend: classifyTrend(prior.score, recent.score),
    });
  }
  // Best-first: highest score, then most-evaluated (more confident), then name.
  perAgent.sort((a, b) => {
    const sa = a.score ?? Number.NEGATIVE_INFINITY;
    const sb = b.score ?? Number.NEGATIVE_INFINITY;
    if (sb !== sa) return sb - sa;
    if (b.evaluatedCount !== a.evaluatedCount) return b.evaluatedCount - a.evaluatedCount;
    return a.codeName.localeCompare(b.codeName);
  });

  return {
    period,
    bucket,
    period_start: periodStart.toISOString(),
    ma_window_days: EVAL_SCORE_MA_WINDOW_DAYS,
    series,
    perAgent,
    overallEvaluatedCount: trailingStats(all, nowMs).n,
    verdictMix: trailingVerdictMix(all, nowMs),
  };
}
